// @flow
import * as React from 'react';

import { useDispatch } from 'react-redux';
import { Loader, Text, View } from 'reshaped';

import {
  selectCartUUID,
  selectCartOwnerList,
  selectIsLoadedCartOwnerList,
  fetchCartOwnerList,
  selectIsFailureCartOwnerList,
  selectIsFetchingCartOwnerList,
} from '@gandi/cart';
import { Button } from '@gandi/reshaped';
import { useTranslator } from '@gandi/translate';

import { useAppSelector } from 'src/hooks/useAppSelector';

import type { Props } from '../../hooks/useModalType';

import { OwnerSelectModal } from './OwnerSelectModal';

export function ResellerOrganizationTab({
  setTabs,
  ...props
}: Props): React.MixedElement {
  const { __ } = useTranslator();
  const dispatch = useDispatch();

  const cartId = useAppSelector(selectCartUUID);
  const invoicesContact = useAppSelector(selectCartOwnerList);

  const isFetchingCartOwners = useAppSelector(selectIsFetchingCartOwnerList);
  const isLoadedCartOwners = useAppSelector(selectIsLoadedCartOwnerList);
  const isFailureCartOwners = useAppSelector(selectIsFailureCartOwnerList);

  React.useEffect(() => {
    setTabs('organizations');
  }, [setTabs]);

  React.useEffect(() => {
    if (!isFetchingCartOwners && !isLoadedCartOwners && !isFailureCartOwners) {
      dispatch(fetchCartOwnerList(cartId));
    }
  }, [
    cartId,
    dispatch,
    isFetchingCartOwners,
    isLoadedCartOwners,
    isFailureCartOwners,
  ]);

  if (isFetchingCartOwners && !isLoadedCartOwners && !isFailureCartOwners) {
    return (
      <View justify="center" direction="row">
        <Loader size="large" ariaLabel={__('Loading...')} />
      </View>
    );
  }

  // When there is only the reseller orga itself as contact, propose to add other ones.
  if (invoicesContact.length <= 1) {
    return (
      <View gap={4}>
        <Text as="p" color="neutral-faded">
          {__(
            'I’m buying products for someone else, and my reseller organization won’t manage them.',
          )}
        </Text>
        <View>
          <Button
            size="large"
            color="primary"
            onClick={() => {
              props.openModal({
                type: 'createAccountModal',
                actionOnSuccess: 'onBack',
              });
            }}
          >
            {__('Add a new contact')}
          </Button>
        </View>
      </View>
    );
  }

  return (
    <View gap={6} height="100%">
      <OwnerSelectModal setTabs={setTabs} {...props} />
    </View>
  );
}
